import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const POLL_MAX = 8;

export default function PaymentResult({ cancelled = false }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (cancelled || !sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      if (++tries >= POLL_MAX) { setState("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled]);

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-result">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
            <h1 className="text-2xl font-display font-bold mt-6">Confirming your payment…</h1>
            <p className="text-muted-foreground mt-2">Hang tight, this only takes a moment.</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
            <h1 className="text-3xl font-display font-extrabold mt-6">You're all set! 🎉</h1>
            <p className="text-muted-foreground mt-2">Your {info?.plan_name ? "" : ""}payment succeeded and your VividVR plan is active.</p>
            <Button data-testid="payment-continue-btn" onClick={() => nav("/dashboard")} className="rounded-full mt-8 h-11 px-6">Go to dashboard <ArrowRight className="w-4 h-4 ml-1" /></Button>
          </div>
        )}
        {(state === "cancelled" || state === "failed" || state === "timeout") && (
          <div data-testid="payment-failed">
            <XCircle className="w-14 h-14 text-muted-foreground mx-auto" />
            <h1 className="text-2xl font-display font-bold mt-6">{state === "cancelled" ? "Checkout cancelled" : "Payment not completed"}</h1>
            <p className="text-muted-foreground mt-2">{state === "timeout" ? "We couldn't confirm your payment yet. If you were charged, it'll appear shortly." : "No charge was made. You can try again anytime."}</p>
            <Button data-testid="payment-retry-btn" onClick={() => nav("/pricing")} variant="outline" className="rounded-full mt-8 h-11 px-6">Back to pricing</Button>
          </div>
        )}
      </div>
    </div>
  );
}
